import { requestAnimationFrame, cancelAnimationFrame, getItemType, isCloningEvent } from './ADNDHelpers'
import { updateState, getState, DEFAULT_STATE } from './ADNDState'

import ADNDTargetsLayer from './layers/ADNDTargetsLayer'
import ADNDGlassLayer from './layers/ADNDGlassLayer'
import ADNDEdgeScrollingLayer from './layers/ADNDEdgeScrollingLayer'

const props = {
  connected: false,
  started: false,
  /**
   * @type {Array<import('./layers/ADNDDragLayer').Layer>}
   */
  layers: [],
  /**
   * @type {Array<import('./layers/ADNDDragLayer').Layer>}
   */
  animateLayers: [],
  lastCursor: {
    x: null,
    y: null
  },
  leftDocument: false,
  /**
   * @type {number|null}
   */
  frameRequestID: null
}

const CONNECT = {
  drop(event) {
    if (props.started) {
      event.preventDefault()
    }
    ADND().drop(event).stop(event)
  },
  dragOver(event) {
    if (!props.started) {
      ADND().start(event)
    }
    const x = event.pageX || event.clientX
    const y = event.pageY || event.clientY
    updateState(function (state) {
      const operationType =
        state.operation.enableClone && isCloningEvent(event) ? 'clone' : state.operation.enableMove ? 'move' : 'clone'
      if (operationType != state.operation.type) {
        state.operation.type = operationType
        if (null != state.source && state.source.PRIVATE.updateOperationType) {
          state.source.PRIVATE.updateOperationType(operationType)
        }
      }
      if (event.dataTransfer) {
        if (state.target && state.target.current) {
          event.dataTransfer.dropEffect = state.target.current.PRIVATE.getDropEffect
            ? state.target.current.PRIVATE.getDropEffect(operationType)
            : 'move' == operationType
            ? 'move'
            : 'copy'
        } else if (state.source && state.source.PRIVATE.getDropEffect) {
          event.dataTransfer.dropEffect = state.source.PRIVATE.getDropEffect(operationType)
        } else {
          event.dataTransfer.dropEffect = 'none'
        }
      }
      if (x != props.lastCursor.x || y != props.lastCursor.y) {
        state.cursor.x = x
        state.cursor.y = y
        props.lastCursor.x = x
        props.lastCursor.y = y
      }
      return state
    })
    if (props.started) {
      event.preventDefault()
    }
  }
}

function layerFrame(layer) {
  if (layer.updatesState) {
    updateState(layer.frame)
  } else {
    layer.frame(getState())
  }
}

function animationFrame(/*timestamp*/) {
  props.animateLayers.forEach(layerFrame)
  props.frameRequestID = requestAnimationFrame(animationFrame)
}

const MODULE = {
  connect() {
    if (!props.connected) {
      document.addEventListener('drop', CONNECT.drop, true)
      document.addEventListener('dragover', CONNECT.dragOver, true)
      props.connected = true
    }
    return MODULE
  },
  disconnect() {
    if (props.connected) {
      document.removeEventListener('drop', CONNECT.drop, true)
      document.removeEventListener('dragover', CONNECT.dragOver, true)
      props.connected = false
      MODULE.stop()
    }
    return MODULE
  },
  isConnected() {
    return props.connected
  },
  start(event) {
    if (!props.started) {
      props.started = true
      props.leftDocument = false
      const state = updateState(function (state) {
        const itemType = getItemType(state, event)
        const defaultState = DEFAULT_STATE()
        return null == itemType
          ? defaultState
          : {
              ...defaultState,
              source: state.source,
              item: {
                ...defaultState.item,
                type: itemType,
                width: state.item.width,
                height: state.item.height
              },
              stage: 'started',
              cursor: {
                ...defaultState.cursor,
                x: state.cursor.x,
                y: state.cursor.y
              },
              layers: {
                ...defaultState.layers,
                glass: state.layers.glass,
                edgeScrolling: state.layers.edgeScrolling
              },
              operation: state.operation
            }
      })
      if (null != state.item.type) {
        if (false !== state.layers.glass) {
          MODULE.addLayer(ADNDGlassLayer())
        }
        MODULE.addLayer(ADNDTargetsLayer())
        if (false !== state.layers.edgeScrolling) {
          MODULE.addLayer(ADNDEdgeScrollingLayer())
        }
        props.layers.forEach(function (layer) {
          layer.start(event)
        })
        props.frameRequestID = requestAnimationFrame(animationFrame)
      } else {
        props.started = false
      }
    }
    return MODULE
  },
  stop(event) {
    if (props.started) {
      props.started = false
      props.leftDocument = false
      updateState(function (state) {
        if ('dropped' != state.stage) {
          state.stage = 'canceled'
        }
        state.operation = DEFAULT_STATE().operation
        return state
      })
      if (null != props.frameRequestID) {
        cancelAnimationFrame(props.frameRequestID)
        props.frameRequestID = null
      }
      props.layers.forEach(function (layer) {
        layer.stop(event)
      })
      props.layers = []
      props.animateLayers = []
      props.lastCursor.x = null
      props.lastCursor.y = null
    }
    return MODULE
  },
  isStarted() {
    return props.started
  },
  addLayer(layer) {
    if (!props.layers.includes(layer)) {
      props.layers.push(layer)
    }
    if (layer.frame && !props.animateLayers.includes(layer)) {
      props.animateLayers.push(layer)
    }
    return MODULE
  },
  removeLayer(layer) {
    const layerIndex = props.layers.indexOf(layer)
    if (layerIndex >= 0) {
      props.layers.splice(layerIndex, 1)
    }
    const animateLayerIndex = props.animateLayers.indexOf(layer)
    if (animateLayerIndex >= 0) {
      props.animateLayers.splice(animateLayerIndex, 1)
    }
    return MODULE
  },
  drop(event) {
    const state = getState()
    props.layers.forEach(function (layer) {
      if (layer.drop) {
        layer.drop(state, event)
      }
    })
    return MODULE
  },
  leftDocument(event) {
    if (!props.leftDocument) {
      props.leftDocument = true
      props.layers.forEach(function (layer) {
        if (layer.leftDocument) {
          layer.leftDocument(event)
        }
      })
      if (null == getState().source) {
        MODULE.stop(event)
      }
    }
    return MODULE
  },
  enteredDocument(event) {
    if (props.leftDocument) {
      props.leftDocument = false
      props.layers.forEach(function (layer) {
        if (layer.enteredDocument) {
          layer.enteredDocument(event)
        }
      })
    }
    return MODULE
  }
}

function ADND() {
  return MODULE
}

// targets

import ADNDDropDataTarget from './targets/ADNDDropDataTarget.js'
import ADNDDropFilesTarget from './targets/ADNDDropFilesTarget.js'
import ADNDItemsList from './targets/ADNDItemsList.js'

ADND.targets = {
  data: ADNDDropDataTarget,
  files: ADNDDropFilesTarget,
  itemsList: ADNDItemsList
}

// sources
import ADNDDataSource from './sources/ADNDDataSource.js'
import ADNDDraggableDataSource from './sources/ADNDDraggableDataSource.js'
import ADNDListItemHandle from './sources/itemsList/ADNDListItemHandle.js'

ADND.sources = {
  data: ADNDDataSource,
  draggableData: ADNDDraggableDataSource,
  listItemHandle: ADNDListItemHandle
}

// module

export default ADND
